import React, { useState } from 'react';

import { Item } from '@baemingo/order-status-events/dist/types';
import styled from '@emotion/styled';

import Icon from '~ComponentsImproved/Icon';
import Loader from '~ComponentsImproved/Loader';
import Modal from '~ComponentsImproved/Modal';
import { useI18n, useLogger } from '~Hooks';
import { Box, Card } from '~Styles';
import PaymentHandler from './PaymentHandler';
import StoreFrontIcon from '~Assets/icons/storefront.svg';
import { GetGiftCardItem_item } from '~Queries/__queries__';
import { PaymentMethod } from '~reducers';
/**
 * This is a generic payment modal. Provide a callback to take your action on success or fail
 */

const ModalContainer = styled(Card)`
  height: auto;
  overflow-x: hidden;
  min-height: 400px;
  background: transparent;
  width: 30vw;
  max-height: 80vh;
`;

const ModalHeader = styled(Box)<{ state: TransactionState }>`
  transition: background 0.2s ease;
  background: ${({ state }) => StateColor[state]};
  position: 'relative';
  flex: 1;
  color: var(--colorBlueGray100);
`;

const ModalContent = styled(Box)`
  overflow-y: scroll;
  background: var(--colorBlueGray1200);
  color: var(--colorBlueGray100);
  flex-grow: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
`;

type TransactionState = 'IDLE' | 'PENDING' | 'AWAITING_TRANSACTION' | 'SUCCESS' | 'ERROR';

const StateColor = {
  IDLE: 'linear-gradient(140deg, rgba(22, 66, 190, 1) 0%, rgba(1, 33, 122, 1) 100%)',
  PENDING: 'linear-gradient(140deg, rgba(22, 66, 190, 1) 0%, rgba(1, 33, 122, 1) 100%)',
  AWAITING_TRANSACTION:
    'linear-gradient(140deg, rgba(22, 66, 190, 1) 0%, rgba(1, 33, 122, 1) 100%)',
  SUCCESS: 'linear-gradient(140deg, var(--colorStatusGreen) 0%, var(--colorUIGreen) 100%)',
  ERROR: 'linear-gradient(140deg, #ffa502 0%, #ff8400 100%)',
};

interface Transaction {
  amount: number;
  item: GetGiftCardItem_item;
}

export interface PaymentOptions {
  methods?: PaymentMethod[];
  disableTip?: boolean;
}

interface PaymentModalProps {
  onSuccess: () => void;
  onError: (e: Error) => void;
  onAbort: () => void;
  icon?: string;
  transaction: Transaction;
  zIndex?: number;
  options: PaymentOptions;
}

export default function PaymentModal({
  onSuccess,
  onError,
  onAbort,
  icon,
  transaction,
  options,
  zIndex = 99,
}: PaymentModalProps) {
  const { i18n } = useI18n();
  const logger = useLogger('payment');
  const [transactionState, setTransactionState] = useState<TransactionState>(
    'AWAITING_TRANSACTION',
  );

  const handleSuccess = () => {
    setTransactionState('SUCCESS');
    setTimeout(() => {
      onSuccess();
    }, 1000);
  };

  const handleError = (e: Error) => {
    logger.error('Payment failed', {
      errorMessage: e.message,
    });
    setTransactionState('ERROR');
    setTimeout(() => {
      onError(e);
    }, 1000);
  };

  if (!transaction.item) {
    handleError(new Error('Initiated payment with nullish item'));
  }

  return (
    <Modal isShowing={true} hide={onAbort} zIndex={zIndex}>
      <ModalContainer padding="none" boxShadow="hard" display="flex" flexDirection="column">
        <ModalHeader
          display="flex"
          justifyContent="center"
          fluid
          alignItems="center"
          state={transactionState}>
          {icon && (
            <Icon
              src={icon ?? StoreFrontIcon}
              height="120px"
              width="120px"
              color={'var(--colorBlueGray100)'}
              style={{
                position: 'absolute',
                opacity: 0.05,
              }}
            />
          )}
          <h2>Payment</h2>
        </ModalHeader>
        <ModalContent padding="xl">
          {transactionState === 'PENDING' && <Loader showDimmer={false} />}
          {transactionState === 'AWAITING_TRANSACTION' && (
            <PaymentHandler
              {...transaction}
              onSuccess={handleSuccess}
              onError={handleError}
              options={options}
            />
          )}
          {transactionState === 'SUCCESS' && <p>{i18n('General.Success')}!</p>}
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
}
